import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, HelpCircle, AlertTriangle, Shield, FileText, ScrollText, Printer, Download, Mail } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import PageFrame from '../components/PageFrame';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  accident_prevention: Shield,
  after_accident: AlertTriangle,
  regulations: FileText,
  basic_claims: ScrollText,
};

const FAQ = () => {
  const [faqs, setFaqs] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set(['accident_prevention']));
  const [loading, setLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pendingPrint, setPendingPrint] = useState(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handlePrintRequest = (question, categoryTitle, categoryId) => {
    setPendingPrint({ question, categoryTitle, categoryId });
    setShowEmailModal(true);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Save email to database
      await axios.post(`${API}/faq/download`, { 
        email, 
        category: pendingPrint.categoryId,
        question_id: pendingPrint.question.id,
        source: 'faq_question_poster' 
      });
      
      toast.success("Opening printable poster...");
      setShowEmailModal(false);
      
      // Now print the question
      printQuestion(pendingPrint.question, pendingPrint.categoryTitle);
      
      setEmail('');
      setPendingPrint(null);
    } catch (error) {
      toast.error("Failed to process request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Format answer text for print
  const formatAnswer = (answer) => {
    return answer
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n- /g, '</p><p class="bullet">• ')
      .replace(/\n\d+\. /g, (match) => `</p><p class="numbered">${match.trim()} `)
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br/>');
  };

  const printQuestion = (question, categoryTitle) => {
    const printWindow = window.open('', '_blank');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${question.question} - TruckAccidentSupport.com</title>
        <style>
          @page { margin: 0.5in; size: letter; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 1.5;
            color: #333;
            padding: 15px;
          }
          .page-frame {
            border: 3px solid #d1d5db;
            border-radius: 16px;
            padding: 25px 30px;
            position: relative;
            background: #fff;
            min-height: calc(100vh - 30px);
          }
          .corner-tl, .corner-tr, .corner-bl, .corner-br {
            position: absolute;
            width: 60px;
            height: 60px;
            border-color: #ea580c;
            border-style: solid;
          }
          .corner-tl { top: -3px; left: -3px; border-width: 6px 0 0 6px; border-radius: 16px 0 0 0; }
          .corner-tr { top: -3px; right: -3px; border-width: 6px 6px 0 0; border-radius: 0 16px 0 0; }
          .corner-bl { bottom: -3px; left: -3px; border-width: 0 0 6px 6px; border-radius: 0 0 0 16px; }
          .corner-br { bottom: -3px; right: -3px; border-width: 0 6px 6px 0; border-radius: 0 0 16px 0; }
          .logo-watermark {
            position: absolute;
            top: 12px;
            left: 18px;
            display: flex;
            align-items: center;
            gap: 8px;
            opacity: 0.15;
          }
          .logo-watermark svg { width: 24px; height: 24px; fill: #ea580c; }
          .logo-watermark span { font-size: 12px; font-weight: bold; color: #666; }
          .header {
            text-align: center;
            margin: 25px 0 20px 0;
            padding-bottom: 15px;
            border-bottom: 3px solid #ea580c;
          }
          .header-top {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            margin-bottom: 10px;
          }
          .header-top svg { width: 36px; height: 36px; fill: #ea580c; }
          .category-badge {
            display: inline-block;
            background: linear-gradient(135deg, #ea580c 0%, #f97316 100%);
            color: white;
            padding: 6px 16px;
            border-radius: 20px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .header .tagline { 
            font-size: 11px; 
            color: #ea580c; 
            margin-top: 8px;
            font-style: italic;
          }
          .question-section {
            margin: 25px 0;
          }
          .question-box {
            background: linear-gradient(135deg, #ea580c 0%, #f97316 100%);
            color: white;
            padding: 20px 25px;
            border-radius: 12px;
            margin-bottom: 20px;
          }
          .question-label {
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 2px;
            opacity: 0.9;
            margin-bottom: 8px;
          }
          .question-text {
            font-size: 18px;
            font-weight: 700;
            line-height: 1.4;
          }
          .answer-box {
            background: #f8f9fa;
            border: 2px solid #e5e5e5;
            border-radius: 12px;
            padding: 25px;
          }
          .answer-label {
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 2px;
            color: #ea580c;
            font-weight: 600;
            margin-bottom: 15px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .answer-label::after {
            content: '';
            flex: 1;
            height: 2px;
            background: linear-gradient(to right, #ea580c, transparent);
          }
          .answer-content {
            font-size: 13px;
            line-height: 1.7;
            color: #333;
          }
          .answer-content p { margin-bottom: 12px; }
          .answer-content p:last-child { margin-bottom: 0; }
          .answer-content .bullet { 
            padding-left: 20px;
            margin-bottom: 8px;
            position: relative;
          }
          .answer-content .numbered { 
            padding-left: 20px;
            margin-bottom: 8px;
          }
          .answer-content strong { 
            color: #ea580c; 
            font-weight: 700;
          }
          .footer {
            margin-top: 25px;
            padding-top: 15px;
            border-top: 2px solid #ea580c;
            text-align: center;
          }
          .footer-logo {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            margin-bottom: 8px;
          }
          .footer-logo svg { width: 26px; height: 26px; fill: #ea580c; }
          .footer-logo span { font-size: 15px; font-weight: bold; color: #333; }
          .footer-tagline { font-size: 11px; color: #ea580c; font-weight: 500; }
          .footer-url { font-size: 10px; color: #666; margin-top: 3px; }
          .footer-disclaimer { 
            font-size: 9px; 
            color: #999; 
            margin-top: 12px;
            font-style: italic;
          }
          .print-note {
            text-align: center;
            font-size: 11px;
            color: #ea580c;
            margin-top: 15px;
            font-weight: 600;
            background: #fff7ed;
            padding: 10px;
            border-radius: 8px;
            border: 1px dashed #ea580c;
          }
          .footer-bottom {
            position: absolute;
            bottom: 10px;
            right: 15px;
            display: flex;
            align-items: center;
            gap: 5px;
            opacity: 0.12;
          }
          .footer-bottom svg { width: 16px; height: 16px; fill: #ea580c; }
          .footer-bottom span { font-size: 9px; color: #666; }
          @media print {
            body { padding: 0; }
            .page-frame { border-width: 3px; min-height: auto; }
          }
        </style>
      </head>
      <body>
        <div class="page-frame">
          <div class="corner-tl"></div>
          <div class="corner-tr"></div>
          <div class="corner-bl"></div>
          <div class="corner-br"></div>
          
          <div class="logo-watermark">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>
            <span>TruckAccidentSupport</span>
          </div>
          
          <div class="header">
            <div class="header-top">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>
            </div>
            <span class="category-badge">${categoryTitle}</span>
            <p class="tagline">TruckAccidentSupport.com - Accident & Safety Resources</p>
          </div>
          
          <div class="question-section">
            <div class="question-box">
              <div class="question-label">Question</div>
              <div class="question-text">${question.question}</div>
            </div>
            
            <div class="answer-box">
              <div class="answer-label">Answer</div>
              <div class="answer-content">
                <p>${formatAnswer(question.answer)}</p>
              </div>
            </div>
          </div>
          
          <p class="print-note">📌 Print this and keep it in your cab for quick reference</p>
          
          <div class="footer">
            <div class="footer-logo">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>
              <span>TruckAccidentSupport</span>
            </div>
            <p class="footer-tagline">Accident & Safety Resources</p>
            <p class="footer-url">www.truckaccidentsupport.com</p>
            <p class="footer-disclaimer">This information is for educational purposes only and does not constitute legal advice.</p>
          </div>
          
          <div class="footer-bottom">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>
            <span>truckaccidentsupport.com</span>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 300);
    };
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/faq/all`);
      setFaqs(response.data.faqs);
    } catch (error) {
      toast.error("Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults(null);
      return;
    }

    try {
      const response = await axios.get(`${API}/faq/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data.results);
      const newExpanded = new Set(response.data.results.map(r => r.id));
      setExpandedItems(newExpanded);
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  const toggleItem = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const renderAnswer = (answer) => {
    return answer.split('\n').map((line, index) => {
      line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      if (line.startsWith('- ')) {
        return (
          <li key={index} className="ml-4 mb-1" dangerouslySetInnerHTML={{ __html: line.substring(2) }} />
        );
      }
      if (line.match(/^\d+\./)) {
        return (
          <li key={index} className="ml-4 mb-1 list-decimal" dangerouslySetInnerHTML={{ __html: line.replace(/^\d+\./, '') }} />
        );
      }
      if (line.trim() === '') {
        return <br key={index} />;
      }
      return (
        <p key={index} className="mb-2" dangerouslySetInnerHTML={{ __html: line }} />
      );
    });
  };

  if (loading) {
    return (
      <PageFrame title="Loading...">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </PageFrame>
    );
  }

  return (
    <>
      <SEO 
        title="Frequently Asked Questions for Truck Drivers"
        description="FAQs for truck drivers and trucking companies - accident prevention, what to do after an accident, trucking regulations, and basic claims information."
      />
      
      <PageFrame 
        title="Frequently Asked Questions"
        subtitle="Resources for truck drivers and trucking companies - safety, regulations, and guidance."
      >
        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-10">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-12 py-6 text-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-xl"
            />
          </div>
          {searchResults && (
            <p className="mt-2 text-sm text-gray-500">
              Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
          )}
        </div>

        {/* Search Results */}
        {searchResults ? (
          <div className="space-y-4">
            {searchResults.length > 0 ? (
              searchResults.map((item) => (
                <Card key={item.id} className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => toggleItem(item.id)}
                    className="w-full p-4 flex items-start justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-start gap-3 flex-1">
                      <HelpCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-1" />
                      <div>
                        <span className="text-xs text-orange-500 font-medium">{item.category_title}</span>
                        <h3 className="font-semibold text-gray-900 dark:text-white">{item.question}</h3>
                      </div>
                    </div>
                    {expandedItems.has(item.id) ? (
                      <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {expandedItems.has(item.id) && (
                    <CardContent className="pt-0 pb-4 px-4">
                      <div className="ml-8 text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none">
                        {renderAnswer(item.answer)}
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))
            ) : (
              <div className="text-center py-8">
                <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No questions found matching "{searchQuery}"</p>
                <button 
                  onClick={() => { setSearchQuery(''); setSearchResults(null); }}
                  className="mt-2 text-orange-500 hover:text-orange-400"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Category View */
          <div className="space-y-6">
            {faqs && Object.entries(faqs).map(([categoryId, category]) => {
              const Icon = CATEGORY_ICONS[categoryId] || HelpCircle;
              const isExpanded = expandedCategories.has(categoryId);
              
              return (
                <Card key={categoryId} className="bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-4 flex items-center justify-between">
                    <button
                      onClick={() => toggleCategory(categoryId)}
                      className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
                    >
                      <div className="p-2 rounded-lg bg-orange-500/20">
                        <Icon className="h-6 w-6 text-orange-500" />
                      </div>
                      <div className="text-left">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{category.title}</h2>
                        <p className="text-sm text-gray-500">{category.questions.length} questions</p>
                      </div>
                    </button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleCategory(categoryId)}>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="border-t border-gray-200 dark:border-gray-700">
                      {category.questions.map((item) => (
                        <div key={item.id} className="border-b border-gray-100 dark:border-gray-700/50 last:border-b-0">
                          <button
                            onClick={() => toggleItem(item.id)}
                            className="w-full p-4 flex items-start justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                          >
                            <div className="flex items-start gap-3 flex-1">
                              <HelpCircle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                              <h3 className="font-medium text-gray-800 dark:text-gray-200">{item.question}</h3>
                            </div>
                            {expandedItems.has(item.id) ? (
                              <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
                            )}
                          </button>
                          {expandedItems.has(item.id) && (
                            <div className="px-4 pb-4">
                              <div className="ml-8 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                                {renderAnswer(item.answer)}
                              </div>
                              <div className="ml-8 mt-3 flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handlePrintRequest(item, category.title, categoryId)}
                                  className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10 hover:border-orange-500"
                                  data-testid={`print-question-${item.id}`}
                                >
                                  <Printer className="h-4 w-4 mr-1" />
                                  Print This Q&A
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Still Have Questions */}
        <Card className="mt-10 bg-orange-500/10 border-orange-500/30">
          <CardContent className="p-6 text-center">
            <HelpCircle className="h-10 w-10 text-orange-500 mx-auto mb-3" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Still Have Questions?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Can't find what you're looking for? Check out our other resources.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a 
                href="/accident-checklist" 
                className="inline-flex items-center justify-center px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Accident Checklist
              </a>
              <a 
                href="/resources" 
                className="inline-flex items-center justify-center px-6 py-2 border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-500/10 transition-colors"
              >
                Safety Resources
              </a>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Note: This information is for educational purposes only and is not legal advice. 
              Consult a qualified professional for specific situations.
            </p>
          </CardContent>
        </Card>

        {/* Email Modal */}
        <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
          <DialogContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-white flex items-center gap-2">
                <Printer className="h-5 w-5 text-orange-500" />
                Print Q&A Poster
              </DialogTitle>
              <DialogDescription className="text-gray-600 dark:text-gray-400">
                <span className="block mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded text-sm text-orange-700 dark:text-orange-300 italic">
                  "{pendingPrint?.question?.question?.slice(0, 80)}{pendingPrint?.question?.question?.length > 80 ? '...' : ''}"
                </span>
                <span className="block mt-2">
                  Enter your email to print this Q&A poster. We'll also send you safety updates.
                </span>
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEmailSubmit} className="space-y-4 mt-4">
              <div>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="faq-email-input"
                  className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-orange-500 hover:bg-orange-600"
                disabled={submitting}
                data-testid="faq-submit-button"
              >
                {submitting ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Get Printable Poster
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                We'll send you safety tips and updates. Unsubscribe anytime.
              </p>
            </form>
          </DialogContent>
        </Dialog>
      </PageFrame>
    </>
  );
};

export default FAQ;
